<script setup lang="ts">
/**
 * 积分充值弹窗组件
 * 套餐横向排列，选中即显示二维码（PC）或跳转 H5 支付（移动端）
 * 移动端通过 processPayment 自动判断环境并跳转
 */
import { ref, computed, onUnmounted, watch } from 'vue';
import { Modal, Spin, Button, message } from 'ant-design-vue';
import {
  ThunderboltFilled, CheckCircleFilled, CloseCircleFilled,
  SafetyOutlined, ClockCircleOutlined, StarOutlined,
  FileTextOutlined, CustomerServiceOutlined
} from '@ant-design/icons-vue';
import { useIdentityCredits } from '@/composables/useIdentityCredits';
import { getCSDClient } from '@/lib/csd-adapter';
import { getDeviceEnvironment } from '@/lib/csd-adapter';

/** 套餐类型常量：积点产品 */
const PRICE_PLAN_TYPE_POINT = 4;

/** 是否为手机设备（基于 UA 判断，比 innerWidth 更准确） */
const isMobileDevice = computed(() => getDeviceEnvironment().includes('phone'));

/** Props */
const props = defineProps<{ open: boolean }>();

/** Emits */
const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'success'): void;
}>();

const csd = getCSDClient();
const { displayBalance } = useIdentityCredits();

/** 套餐类型 */
interface PointPlan {
  id: number;
  name: string;
  price: number;
  originalPrice: number;
  points: number;
  bonusPoints: number;
  recommended?: boolean;
}

function convertPackageToPlan(pkg: any, index: number, total: number): PointPlan {
  const price = pkg.price || 0;
  const originalPrice = pkg.line_price || price;
  const points = pkg.number || 0;
  const planName = pkg.plan_name || `${points}积分包`;
  const baseMatch = planName.match(/(\d+)/);
  const basePoints = baseMatch ? parseInt(baseMatch[1], 10) : points;
  const bonusPoints = points > basePoints ? points - basePoints : 0;
  return { id: pkg.id, name: planName, price, originalPrice, points, bonusPoints, recommended: index === total - 1 };
}

const plans = ref<PointPlan[]>([]);
const plansLoading = ref(false);

type PaymentStep = 'select' | 'success' | 'failed';
const paymentStep = ref<PaymentStep>('select');
const selectedPlan = ref<PointPlan | null>(null);
const qrCodeUrl = ref('');
const orderId = ref<string>('');
const paymentLoading = ref(false);

const currentCredits = computed(() => displayBalance.value);
const savedAmount = computed(() => {
  if (!selectedPlan.value) {return 0;}
  return selectedPlan.value.originalPrice - selectedPlan.value.price;
});

async function loadPlans() {
  if (!csd) {return;}
  plansLoading.value = true;
  try {
    const packages = await csd.paymentService.getPointPackages();
    if (packages?.length > 0) {
      const sorted = [...packages].sort((a, b) => (a.price || 0) - (b.price || 0));
      plans.value = sorted.map((pkg, i) => convertPackageToPlan(pkg, i, sorted.length));
    }
  } catch (error: any) {
    message.error('获取套餐列表失败，请稍后重试');
  } finally {
    plansLoading.value = false;
  }
}

async function handleSelectPlan(plan: PointPlan) {
  if (paymentLoading.value && selectedPlan.value?.id === plan.id) {return;}
  selectedPlan.value = plan;
  await loadPayment(plan);
}

/**
 * 加载支付信息
 * PC 端：调 processPayment 获取二维码并展示
 * 移动端：只创建订单拿 orderId，不自动跳转（等用户点"去支付"）
 */
async function loadPayment(plan: PointPlan) {
  if (!csd) { message.error('SDK 初始化失败，请刷新页面'); return; }
  if (!csd.isLogin()) { message.warning('请先登录后再充值'); return; }

  csd.paymentService.stopPaymentPolling();
  paymentLoading.value = true;
  qrCodeUrl.value = '';

  try {
    const qrCode = await csd.paymentService.getPaymentQRCode({
      plan_type: PRICE_PLAN_TYPE_POINT,
      goods_id: plan.id,
      coupon_code: '',
    });

    orderId.value = qrCode.order_id;

    // 移动端：只保存订单信息，等用户点击"去支付"再跳转
    if (isMobileDevice.value) {
      return;
    }

    // PC 端：通过 processPayment 获取二维码图片并展示
    const result = await csd.paymentService.processPayment(qrCode);
    if (result.type === 'pc') {
      qrCodeUrl.value = result.qrCodeImage || qrCode.qr_code_url || '';
      if (!qrCodeUrl.value) { message.warning('获取支付二维码失败，请重试'); return; }
      startPaymentPoll();
    }
  } catch (error: any) {
    message.error(error?.message || '获取支付信息失败');
  } finally {
    paymentLoading.value = false;
  }
}

/** 移动端点击"去支付"按钮，调 processPayment 跳转 H5 支付页 */
async function handleMobilePay() {
  if (!csd || !selectedPlan.value) {return;}
  paymentLoading.value = true;
  try {
    const qrCode = await csd.paymentService.getPaymentQRCode({
      plan_type: PRICE_PLAN_TYPE_POINT,
      goods_id: selectedPlan.value.id,
      coupon_code: '',
    });
    orderId.value = qrCode.order_id;
    await csd.paymentService.processPayment(qrCode);
  } catch (error: any) {
    message.error(error?.message || '跳转支付失败，请重试');
  } finally {
    paymentLoading.value = false;
  }
}

async function startPaymentPoll() {
  if (!orderId.value || !csd) {return;}
  try {
    await csd.paymentService.startPaymentPolling(
      orderId.value,
      async () => {
        paymentStep.value = 'success';
        await creditsStore.refreshBalance();
        emit('success');
      },
      () => { console.log('[充值] 支付轮询超时'); }
    );
  } catch (error: any) {
    console.error('[充值] 支付轮询失败:', error);
  }
}

function handleClose() {
  csd?.paymentService.stopPaymentPolling();
  emit('update:open', false);
  setTimeout(() => {
    paymentStep.value = 'select';
    qrCodeUrl.value = '';
    orderId.value = '';
  }, 300);
}

function handleContinue() {
  paymentStep.value = 'select';
  if (selectedPlan.value) {loadPayment(selectedPlan.value);}
}

watch(() => props.open, async (open) => {
  if (open) {
    await loadPlans();
    if (plans.value.length > 0) {
      selectedPlan.value = plans.value[0];
      await loadPayment(plans.value[0]);
    }
  }
});

onUnmounted(() => { csd?.paymentService.stopPaymentPolling(); });
</script>

<template>
  <Modal
    :open="props.open"
    :footer="null"
    :width="isMobileDevice ? '100%' : 860"
    :closable="true"
    :mask-closable="true"
    centered
    :class="'recharge-modal'"
    @cancel="handleClose"
  >
    <!-- 选择套餐 -->
    <div v-if="paymentStep === 'select'" class="recharge-content">
      <!-- 标题 -->
      <div class="modal-header">
        <div class="title-row">
          <ThunderboltFilled class="title-icon" />
          <h2 class="title">积分充值</h2>
        </div>
        <div class="balance-info" :style="!isMobileDevice ? {marginRight:'26px'} : {}">
          当前余额：<span class="balance-value">{{ currentCredits >= 0 ? currentCredits : '--' }}</span> 积分
        </div>
      </div>

      <!-- 套餐列表 -->
      <div class="plans-section">
          <div v-if="plans.length > 0" class="plans-grid">
            <div
              v-for="plan in plans"
              :key="plan.id"
              class="plan-card"
              :class="{ selected: selectedPlan?.id === plan.id }"
              @click="handleSelectPlan(plan)"
            >
              <div v-if="selectedPlan?.id === plan.id" class="check-mark">
                <CheckCircleFilled />
              </div>
              <!-- 左侧：套餐名 + 价格 -->
              <div class="plan-left">
                <div class="plan-header">
                  <ThunderboltFilled class="plan-icon" />
                  <span class="plan-name">{{ plan.name }}</span>
                  <span v-if="plan.bonusPoints > 0" class="bonus-tag">再送{{ plan.bonusPoints }}积分</span>
                </div>
                <div class="plan-price-area">
                  <span class="current-price">¥{{ plan.price }}<small>元</small></span>
                  <span class="original-price">原价 ¥{{ plan.originalPrice }}</span>
                </div>
              </div>
              <!-- 右侧：权益说明 -->
              <div class="plan-benefits">
                <div class="benefit-item"><span class="dot"></span>共计获得 <strong>{{ plan.points }}</strong> 积分</div>
                <div class="benefit-item"><span class="dot"></span>充值积分永不过期</div>
                <div class="benefit-item"><span class="dot"></span>可用于全站所有功能</div>
              </div>            </div>
          </div>
          <div v-else-if="!plansLoading" class="plans-empty"><span>暂无可用套餐</span></div>
        </div>

      <!-- 支付区域 -->
      <div v-show="selectedPlan" class="payment-area" :class="isMobileDevice ? 'payment-area--mobile' : 'payment-area--pc'">
        <!-- 移动端：去支付按钮 -->
        <template v-if="isMobileDevice">
          <div class="mobile-payment">
            <div class="mobile-order-summary">
              <div class="summary-left">
                <div class="summary-row">
                  <span class="label">{{ selectedPlan?.name }}</span>
                  <span class="value-points"><ThunderboltFilled /> {{ selectedPlan?.points }} 积分</span>
                </div>
                <div v-if="selectedPlan?.bonusPoints" class="summary-row">
                  <span class="label">额外赠送</span>
                  <span class="value-bonus">+{{ selectedPlan?.bonusPoints }} 积分</span>
                </div>
              </div>
              <div class="summary-price">¥{{ selectedPlan?.price }}<small>元</small></div>
            </div>
            <Spin :spinning="paymentLoading">
              <Button type="primary" size="large" block @click="handleMobilePay">
                ¥{{ selectedPlan?.price }} 去支付
              </Button>
            </Spin>
          </div>
        </template>

        <!-- PC 端：二维码 + 订单信息 + 价格 -->
        <template v-else>
          <div class="qrcode-section">
            <div class="qrcode-wrapper">
              <Spin :spinning="paymentLoading">
                <div class="qrcode-box">
                  <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="支付二维码" class="qrcode-img" />
                  <div v-else class="qrcode-placeholder"><span>加载中...</span></div>
                </div>
              </Spin>
              <div class="scan-hint"><span class="wechat-icon">💳</span>微信扫码支付</div>
            </div>
          </div>
          <div class="order-info">
            <div class="order-title">订单信息</div>
            <div class="order-details">
              <div class="detail-item"><span class="label">套餐名称</span><span class="value">{{ selectedPlan?.name }}</span></div>
              <div v-if="selectedPlan?.bonusPoints" class="detail-item">
                <span class="label">额外赠送</span><span class="value bonus-value">+{{ selectedPlan?.bonusPoints }} 积分</span>
              </div>
              <div class="detail-item total">
                <span class="label">共计获得</span>
                <span class="value points"><ThunderboltFilled /> {{ selectedPlan?.points }} 积分</span>
              </div>
            </div>
          </div>
          <div class="price-section">
            <div class="price-label">应付金额</div>
            <div class="price-main"><span class="currency">¥</span><span class="amount">{{ selectedPlan?.price }}</span></div>
            <div class="price-original">原价 <span>¥{{ selectedPlan?.originalPrice }}</span></div>
            <div class="save-badge"><span class="save-icon">🎉</span>已省 ¥{{ savedAmount }}</div>
          </div>
        </template>
      </div>

      <!-- 底部保障 -->
      <div class="footer-info">
        <div class="guarantee-badges">
          <span class="badge"><SafetyOutlined /> 安全支付</span>
          <span class="badge"><ClockCircleOutlined /> 即时到账</span>
          <span class="badge"><StarOutlined /> 永不过期</span>
          <a class="badge invoice-link" href="https://invoice.hoposoft.com/" target="_blank" rel="noopener noreferrer"><FileTextOutlined /> 发票专区</a>
          <a class="badge invoice-link" href="https://kefu.chaoshidai.net/visitor/GfzO3cbOK93XA-_fQMuPg" target="_blank" rel="noopener noreferrer"><CustomerServiceOutlined /> 联系客服</a>
        </div>
      </div>
    </div>

    <!-- 支付成功 -->
    <div v-else-if="paymentStep === 'success'" class="result-content success">
      <CheckCircleFilled class="result-icon" />
      <div class="result-title">支付成功</div>
      <div class="result-desc">已充值 <strong>{{ selectedPlan?.points }}</strong> 积分</div>
      <div class="result-actions">
        <Button @click="handleContinue">继续充值</Button>
        <Button type="primary" @click="handleClose">完成</Button>
      </div>
    </div>

    <!-- 支付失败 -->
    <div v-else-if="paymentStep === 'failed'" class="result-content failed">
      <CloseCircleFilled class="result-icon" />
      <div class="result-title">支付失败</div>
      <div class="result-desc">请重试或联系客服</div>
      <div class="result-actions">
        <Button @click="handleContinue">重试</Button>
        <Button type="primary" @click="handleClose">关闭</Button>
      </div>
    </div>
  </Modal>
</template>
<style lang="scss">
.recharge-modal {
  .ant-modal-content {
    padding: 0;
  }
}

</style>

<style lang="scss" scoped>
// ── Ant Design Modal 基础覆盖 ──────────────────────────────────────
:deep(.ant-modal-content) {
  background: #1c1c1e;
  border: 1px solid #2a2a2e;
  border-radius: 16px;
  overflow: hidden;
  padding: 0;
}


:deep(.ant-modal-body) { padding: 0; }
:deep(.ant-modal-close) {
  color: #666;
  top: 16px;
  right: 16px;
  &:hover { color: #fff; }
}

// ── 整体容器 ──────────────────────────────────────────────────────
.recharge-content {
  display: flex;
  flex-direction: column;
  height: calc(90vh - 40px);
  max-height: 680px;
  overflow: hidden;

  @media (max-width: 768px) {
    height: calc(88vh - 32px);
    max-height: none;
  }
}

// ── 头部 ──────────────────────────────────────────────────────────
.modal-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #2a2a2e;

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    .title-icon { font-size: 20px; color: #0066ff; }
    .title { font-size: 17px; font-weight: 600; color: #fff; margin: 0; }
  }
  .balance-info {
    font-size: 13px;
    color: #888;
    .balance-value { color: #faad14; font-weight: 600; font-size: 14px; }
  }

  @media (max-width: 768px) {
    padding: 16px 16px 12px;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    .title-row { .title-icon { font-size: 17px; } .title { font-size: 15px; } }
    .balance-info { font-size: 12px; .balance-value { font-size: 13px; } }
  }
}

// ── 套餐列表（可滚动区域）────────────────────────────────────────
.plans-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 12px 20px 0;
  background: #1c1c1e;
  scrollbar-width: thin;
  scrollbar-color: #0066ff transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #0066ff; border-radius: 2px; }
  &::-webkit-scrollbar-track { background: transparent; }
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

.plans-empty {
  text-align: center;
  padding: 32px 0;
  color: #555;
  font-size: 14px;
}

// ── 套餐卡片 ──────────────────────────────────────────────────────
.plan-card {
  position: relative;
  padding: 14px;
  background: #252528;
  border: 1.5px solid #333;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: #0066ff; background: #28282e; }
  &.selected { border-color: #0066ff; background: rgba(0, 102, 255, 0.07); }

  .check-mark {
    position: absolute;
    top: 10px;
    right: 10px;
    color: #0066ff;
    font-size: 17px;
  }

  .plan-header {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 8px;
    flex-wrap: wrap;
    .plan-icon { font-size: 14px; color: #0066ff; }
    .plan-name { font-size: 14px; font-weight: 600; color: #fff; }
    .bonus-tag {
      padding: 2px 6px;
      background: linear-gradient(90deg, #ff6b6b, #ee5a5a);
      color: #fff;
      font-size: 10px;
      font-weight: 500;
      border-radius: 3px;
      white-space: nowrap;
    }
  }

  .plan-price-area {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 10px;
    .current-price {
      font-size: 24px;
      font-weight: 700;
      color: #ff4d4f;
      small { font-size: 12px; font-weight: 400; }
    }
    .original-price { font-size: 12px; color: #555; text-decoration: line-through; }
  }

  .plan-benefits {
    border-top: 1px solid #2e2e32;
    padding-top: 8px;
    .benefit-item {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 12px;
      color: #888;
      margin-bottom: 4px;
      &:last-child { margin-bottom: 0; }
      .dot { width: 3px; height: 3px; background: #0066ff; border-radius: 50%; flex-shrink: 0; margin-top: 5px; }
      strong { color: #0066ff; font-weight: 700; }
    }
  }
}

// ── 支付底栏基础 ──────────────────────────────────────────────────
.payment-area {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  overflow: hidden;

  // PC 端：无卡片包裹，直接横向排列三列，保持原始布局
  &--pc {
    background: #252528;
    border: 1.5px solid #333;
    border-radius: 10px;
    margin: 12px 20px 4px;
  }

  // 移动端：卡片包裹，竖向布局
  &--mobile {
    background: #1c1c1e;
    padding: 10px 14px 4px;

    > .mobile-payment {
      width: 100%;
      background: #252528;
      border: 1.5px solid #333;
      border-radius: 10px;
      overflow: hidden;
    }
  }
}

// ── 移动端支付区 ──────────────────────────────────────────────────
.mobile-payment {
  width: 100%;
  padding: 14px 16px;

  .mobile-order-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding: 10px 14px;
    background: rgba(0, 102, 255, 0.08);
    border: 1px solid rgba(0, 102, 255, 0.18);
    border-radius: 8px;

    .summary-left {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .summary-row {
      display: flex;
      align-items: center;
      gap: 8px;
      .label { font-size: 13px; color: #888; }
      .value-points { font-size: 13px; color: #0066ff; font-weight: 700; }
      .value-bonus { font-size: 12px; color: #52c41a; font-weight: 600; }
    }
    .summary-price {
      font-size: 22px;
      font-weight: 700;
      color: #ff4d4f;
      white-space: nowrap;
      small { font-size: 12px; font-weight: 400; }
    }
  }
}

// ── PC 端：二维码区 ───────────────────────────────────────────────
.qrcode-section {
  flex-shrink: 0;
  padding: 16px 20px;
  border-right: 1px solid #2e2e32;
  display: flex;
  align-items: center;
  justify-content: center;

  .qrcode-wrapper { text-align: center; }
  .qrcode-box {
    width: 120px;
    height: 120px;
    padding: 6px;
    background: #fff;
    border-radius: 8px;
    margin-bottom: 8px;
    .qrcode-img { width: 100%; height: 100%; object-fit: contain; }
    .qrcode-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #999; font-size: 12px; }
  }
  .scan-hint {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 12px;
    color: #666;
  }
}

// ── PC 端：订单信息区 ─────────────────────────────────────────────
.order-info {
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-right: 1px solid #2e2e32;

  .order-title { font-size: 13px; font-weight: 600; color: #aaa; margin-bottom: 12px; }
  .order-details {
    .detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      &:last-child { margin-bottom: 0; }
      &.total {
        padding-top: 8px;
        margin-top: 4px;
        border-top: 1px dashed #2e2e32;
      }
      .label { font-size: 13px; color: #666; }
      .value { font-size: 14px; color: #ddd; font-weight: 500; }
      .value.points { color: #0066ff; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 3px; }
      .bonus-value { color: #52c41a; font-weight: 600; }
    }
  }
}

// ── PC 端：价格区 ─────────────────────────────────────────────────
.price-section {
  flex-shrink: 0;
  width: 160px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  // 用微弱的蓝色背景突出价格区，与卡片边框协调
  background: rgba(0, 102, 255, 0.05);

  .price-label { font-size: 12px; color: #666; margin-bottom: 6px; }
  .price-main {
    display: flex;
    align-items: baseline;
    margin-bottom: 4px;
    .currency { font-size: 16px; font-weight: 600; color: #0066ff; }
    .amount { font-size: 36px; font-weight: 700; color: #0066ff; line-height: 1; }
  }
  .price-original { font-size: 12px; color: #555; margin-bottom: 10px; span { text-decoration: line-through; } }
  .save-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 4px 10px;
    background: rgba(82, 196, 26, 0.08);
    border: 1px solid rgba(82, 196, 26, 0.25);
    border-radius: 20px;
    font-size: 11px;
    color: #52c41a;
  }
}

// ── 底部保障信息 ──────────────────────────────────────────────────
.footer-info {
  flex-shrink: 0;
  padding: 8px 20px 14px;
  background: #1c1c1e;

  .guarantee-badges {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;

    .badge {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      color: #555;
      :deep(.anticon) { font-size: 13px; }
    }
    .invoice-link {
      color: #0066ff;
      text-decoration: none;
      transition: color 0.2s;
      &:hover { color: #3385ff; text-decoration: underline; }
    }

    @media (max-width: 768px) {
      gap: 8px 12px;
    }
  }
}

// ── 结果页 ────────────────────────────────────────────────────────
.result-content {
  padding: 48px 24px;
  text-align: center;
  .result-icon { font-size: 60px; margin-bottom: 16px; }
  &.success .result-icon { color: #52c41a; }
  &.failed .result-icon { color: #ff4d4f; }
  .result-title { font-size: 20px; font-weight: 600; color: #fff; margin-bottom: 8px; }
  .result-desc { font-size: 14px; color: #888; margin-bottom: 24px; strong { color: #0066ff; } }
  .result-actions { display: flex; justify-content: center; gap: 12px; }
}
</style>
